import { FC, useEffect, useMemo, useState } from 'react';
import { CustomButton, CustomModal, InfiniteLoadingSpinner } from 'components';
import { ButtonType } from 'components/custom-button/CustomButton';
import SearchBar from 'components/search/SearchBar';
import { CheckboxTickIcon, CircleCloseIcon, GreyStrokeInfoIcon } from 'assets/icons';
import CustomCheckbox from 'components/custom-checkbox/CustomCheckbox';
import CustomAvatar from 'components/avatar/CustomAvatar';
import { useAppSelector } from 'hooks';
import { selectCurrentTeam } from 'containers/app/appSlice';
// eslint-disable-next-line max-len
import { usePaginatedGetServiceAreasQuery } from 'containers/settings/service-areas/hooks/usePaginatedGetServiceAreasQuery';
import { useLazyGetAssignableMembersQuery } from '../api';
import { ServiceArea } from 'containers/settings/service-areas/serviceAreas.model';
import { AssignableMember } from '../jobs.model';

interface ReassignJobModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpdate: (assignees: string[], serviceAreaId?: string) => Promise<void>;
  isLoading?: boolean;
  selectedJobsCount?: number;
}

interface ServiceAreaWithMembers extends ServiceArea {
  members: AssignableMember[];
}

interface MemberSelection {
  memberId: string;
  serviceAreaId?: string;
}

const ReassignJobModal: FC<ReassignJobModalProps> = ({
  isOpen,
  onClose,
  onUpdate,
  isLoading = false,
  selectedJobsCount = 0
}) => {
  const currentTeam = useAppSelector(selectCurrentTeam);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMemberSelections, setSelectedMemberSelections] = useState<MemberSelection[]>([]);
  const [selectedServiceAreaId, setSelectedServiceAreaId] = useState<string | null>(null);

  // Get service areas
  const { list: serviceAreas, isLoading: isServiceAreasLoading } = usePaginatedGetServiceAreasQuery(null, {
    status: 'ENABLED'
  });

  // Get assignable members
  const [getAssignableMembers, { data: assignableMembersData, isLoading: isAssignableMembersLoading }] =
    useLazyGetAssignableMembersQuery();

  useEffect(() => {
    if (currentTeam?.id && isOpen) {
      getAssignableMembers(currentTeam.id.toString());
    }
  }, [currentTeam?.id, isOpen, getAssignableMembers]);

  // Combine service areas with their assignees and all assignable members
  const serviceAreasWithMembers = useMemo<ServiceAreaWithMembers[]>(() => {
    if (!serviceAreas || !assignableMembersData?.data?.teamMemberList) return [];

    return serviceAreas.map(serviceArea => ({
      ...serviceArea,
      members:
        serviceArea.assignees?.map(assignee => {
          const fullMember = assignableMembersData.data.teamMemberList.find(member => member.id === assignee.id);
          return (
            fullMember || {
              ...assignee,
              merchantId: '',
              colorCode: '',
              profilePictureUrl: assignee.profilePictureURL || ''
            }
          );
        }) || []
    }));
  }, [serviceAreas, assignableMembersData]);

  // Get all assignable members for when service areas are empty
  const allAssignableMembers = useMemo(() => {
    return assignableMembersData?.data?.teamMemberList || [];
  }, [assignableMembersData]);

  // Filter data based on search term
  const filteredData = useMemo(() => {
    if (!searchTerm) {
      return {
        serviceAreas: serviceAreasWithMembers,
        allMembers: allAssignableMembers
      };
    }

    const searchLower = searchTerm.toLowerCase();

    // Filter service areas by name or member names
    const filteredServiceAreas = serviceAreasWithMembers.filter(serviceArea => {
      const serviceAreaNameMatch = serviceArea.name.toLowerCase().includes(searchLower);
      const memberNameMatch = serviceArea.members.some(member => member.name.toLowerCase().includes(searchLower));
      return serviceAreaNameMatch || memberNameMatch;
    });

    // Filter all members by name
    const filteredAllMembers = allAssignableMembers.filter(member => member.name.toLowerCase().includes(searchLower));

    return {
      serviceAreas: filteredServiceAreas,
      allMembers: filteredAllMembers
    };
  }, [searchTerm, serviceAreasWithMembers, allAssignableMembers]);

  const handleMemberSelect = (memberId: string, serviceAreaId?: string) => {
    const existingSelectionIndex = selectedMemberSelections.findIndex(
      selection => selection.memberId === memberId && selection.serviceAreaId === serviceAreaId
    );

    if (existingSelectionIndex !== -1) {
      // Remove this specific selection
      const updatedSelections = selectedMemberSelections.filter((_, index) => index !== existingSelectionIndex);
      setSelectedMemberSelections(updatedSelections);

      // If no members selected, clear service area
      if (updatedSelections.length === 0) {
        setSelectedServiceAreaId(null);
      }
    } else {
      // Add member selection with service area context
      const newSelection: MemberSelection = {
        memberId,
        serviceAreaId
      };
      setSelectedMemberSelections(prev => [...prev, newSelection]);

      // Set service area if this is the first selection or if service area is provided
      if (serviceAreaId && (selectedMemberSelections.length === 0 || !selectedServiceAreaId)) {
        setSelectedServiceAreaId(serviceAreaId);
      }
    }
  };

  const handleUpdate = async () => {
    if (selectedMemberSelections.length === 0) return;

    const memberIds = selectedMemberSelections.map(selection => selection.memberId);
    await onUpdate(memberIds, selectedServiceAreaId || undefined);
  };

  const handleClose = () => {
    setSearchTerm('');
    setSelectedMemberSelections([]);
    setSelectedServiceAreaId(null);
    onClose();
  };

  const renderMemberItem = (member: AssignableMember, serviceAreaId?: string) => {
    // Check if this specific member in this specific service area is selected
    const isSelected = selectedMemberSelections.some(
      selection => selection.memberId === member.id && selection.serviceAreaId === serviceAreaId
    );

    // Disable if selections exist in a different service area
    const isDisabled = selectedServiceAreaId && serviceAreaId && selectedServiceAreaId !== serviceAreaId;

    const itemClasses = `flex items-center gap-3 py-2 border-b border-borderGray cursor-pointer transition-colors ${
      isDisabled ? 'cursor-not-allowed' : 'hover:bg-gray-50'
    }`;

    return (
      <div
        key={`${member.id}-${serviceAreaId || 'no-area'}`}
        className={itemClasses}
        onClick={() => !isDisabled && handleMemberSelect(member.id, serviceAreaId)}>
        <CustomCheckbox
          id={`member-checkbox-${member.id}-${serviceAreaId || 'no-area'}`}
          isChecked={isSelected}
          onClick={() => !isDisabled && handleMemberSelect(member.id, serviceAreaId)}
          disabled={isDisabled}
          width="w-4"
          height="h-4">
          {isSelected && <CheckboxTickIcon className="h-3 w-3 text-primaryBtn" />}
        </CustomCheckbox>

        <div className="flex flex-1 items-center gap-3">
          <CustomAvatar
            src={member.profilePictureUrl || member.profilePictureURL || ''}
            label={member.name.charAt(0).toUpperCase()}
            height="h-10"
            width="w-10"
            background="bg-gray-200"
            fontStyle="text-sbase font-medium text-gray-600"
          />
          <div className="flex gap-5">
            <div className="text-sbase font-normal text-primaryText">{member.name}</div>
            <div className="text-sbase font-normal text-accentText">{member.phone}</div>
          </div>
        </div>
      </div>
    );
  };

  const renderServiceAreaSection = (serviceArea: ServiceAreaWithMembers) => {
    // Check if this service area should be disabled
    const isDisabled = selectedServiceAreaId && selectedServiceAreaId !== serviceArea.id;

    const sectionClasses = `mb-5 transition-opacity ${isDisabled ? 'opacity-50' : 'opacity-100'}`;

    return (
      <div key={serviceArea.id} className={sectionClasses}>
        <div className="mb-3 flex items-center gap-2">
          <h3 className="text-sbase font-semibold text-primaryText">{serviceArea.name}</h3>
        </div>
        <div>
          {serviceArea.members.length > 0 ? (
            serviceArea.members.map(member => renderMemberItem(member, serviceArea.id))
          ) : (
            <div className="text-sbase font-normal text-accentText">No members</div>
          )}
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  const showServiceAreas = serviceAreasWithMembers.length > 0;
  const showInfoText = showServiceAreas;

  return (
    <CustomModal open={isOpen} closeModal={handleClose} width="md:w-[492px]" enableOutsideClick={!isLoading}>
      <div className="w-full rounded-md bg-white p-6">
        <div className="mb-6 flex items-center justify-between">
          <div className="flex flex-col gap-1">
            <div className="text-xl font-bold text-headingGray">Re-assign jobs</div>
            <div className="text-sm text-gray-600">Re-assign members to the selected {selectedJobsCount} jobs</div>
          </div>
          <CircleCloseIcon className="h-6 w-6 cursor-pointer" onClick={handleClose} />
        </div>

        <div className="space-y-2">
          <SearchBar
            id="member-search"
            value={searchTerm}
            handleChange={setSearchTerm}
            placeholder={showServiceAreas ? 'Search members or service area' : 'Search members'}
            wrapperStyle="border border-borderGray rounded-[4px] h-[38px] w-full"
            showSearchIcon={true}
            handleClear={() => setSearchTerm('')}
            showCrossIcon={true}
            inputStyle="bg-transparent"
            autoFocus={true}
            magnifierIconStyle="w-5 h-5"
          />

          <div className="text-sm text-gray-600">
            {selectedMemberSelections.length} member{selectedMemberSelections.length !== 1 ? 's' : ''} selected
          </div>

          <div
            className="customNormalScroll !mt-3 max-h-[463px] min-h-[463px] space-y-4 overflow-y-auto border
           border-borderGray p-3">
            {isServiceAreasLoading || isAssignableMembersLoading ? (
              <div className="flex items-center justify-center py-8">
                <InfiniteLoadingSpinner />
              </div>
            ) : (
              <>
                {showServiceAreas ? (
                  <>{filteredData.serviceAreas.map(serviceArea => renderServiceAreaSection(serviceArea))}</>
                ) : (
                  <div className="space-y-2">{filteredData.allMembers.map(member => renderMemberItem(member))}</div>
                )}
              </>
            )}
          </div>

          {showInfoText && (
            <div className="flex items-start gap-2">
              <div className="mt-0.5 shrink-0">
                <GreyStrokeInfoIcon className="h-4 w-4" />
              </div>
              <div className="text-sbase text-accentText">
                Reassigning to a tech from a different service area will also update the job&apos;s service area.
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <CustomButton
              id="reassign-cancel-btn"
              type={ButtonType.SECONDARY}
              onClick={handleClose}
              disabled={isLoading}
              className="h-10 px-6">
              Cancel
            </CustomButton>
            <CustomButton
              id="reassign-update-btn"
              type={ButtonType.PRIMARY}
              onClick={handleUpdate}
              disabled={selectedMemberSelections.length === 0 || isLoading}
              isLoading={isLoading}
              className="h-10 px-6">
              Update
            </CustomButton>
          </div>
        </div>
      </div>
    </CustomModal>
  );
};

export default ReassignJobModal;
